import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@simplicity/ui";
import { Minus, Pencil, Plus } from "lucide-react";
import type { ConfigRevision } from "../../../configTypes";
import { type SaveEvent } from "./clusterRevisions";
import { fieldLabel } from "./connectorFieldLabels";
import { diffJson, type DiffEntry } from "./diffJson";

interface ConnectorDiffSheetProps {
  saveEvent: SaveEvent | null;
  currentVars: Record<string, unknown>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (varsFromRevision: Record<string, unknown>) => void;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function renderValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "(unset)";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

/**
 * Splits a diff path into a top-level field key and a remaining sub-path:
 *   "baseUrl"           -> { key: "baseUrl",   subPath: "" }
 *   "tables[0].enabled" -> { key: "tables",    subPath: "[0].enabled" }
 *   "schedules.foo"     -> { key: "schedules", subPath: "foo" }
 */
function firstBoundaryIndex(path: string): number {
  const candidates = [path.indexOf("."), path.indexOf("[")].filter((i) => i !== -1);
  if (candidates.length === 0) return -1;
  return Math.min(...candidates);
}

function splitPath(path: string): { key: string; subPath: string } {
  if (!path) return { key: "", subPath: "" };
  const boundary = firstBoundaryIndex(path);
  if (boundary === -1) return { key: path, subPath: "" };
  const key = path.slice(0, boundary);
  const rest = path.slice(boundary);
  // Strip leading dot for nested object segments; keep `[i]` for arrays.
  const subPath = rest.startsWith(".") ? rest.slice(1) : rest;
  return { key, subPath };
}

const KIND_STYLES = {
  added: { border: "border-l-success", icon: Plus, iconClass: "text-success" },
  removed: { border: "border-l-destructive", icon: Minus, iconClass: "text-destructive" },
  changed: { border: "border-l-warning", icon: Pencil, iconClass: "text-warning" },
} as const;

function DiffEntryRow({ entry }: { entry: DiffEntry }) {
  const { key, subPath } = splitPath(entry.path);
  const label = fieldLabel(key);
  const style = KIND_STYLES[entry.kind];
  const Icon = style.icon;

  return (
    <div className={cn("border-l-2 pl-3 py-1 text-xs", style.border)}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <Icon className={cn("h-3.5 w-3.5 shrink-0", style.iconClass)} />
        <span className="font-medium">{label}</span>
        {subPath && <span className="font-mono text-muted-foreground">{subPath}</span>}
      </div>
      <div className="pl-5 font-mono break-all">
        {entry.kind === "changed" && (
          <>
            <span className="text-destructive">{renderValue(entry.previous)}</span>
            <span className="text-muted-foreground"> → </span>
            <span className="text-success">{renderValue(entry.current)}</span>
          </>
        )}
        {entry.kind === "added" && (
          <span className="text-success">{renderValue(entry.current)}</span>
        )}
        {entry.kind === "removed" && (
          <span className="text-destructive">{renderValue(entry.previous)}</span>
        )}
      </div>
    </div>
  );
}

function VarsSection({
  revision,
  currentVars,
  onRestore,
}: {
  revision: ConfigRevision;
  currentVars: Record<string, unknown>;
  onRestore: () => void;
}) {
  const diffs = diffJson(revision.previousValue, currentVars);
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Vars</h3>
        <Button type="button" size="sm" variant="outline" onClick={onRestore}>
          Restore vars
        </Button>
      </div>
      {diffs.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">
          No differences between this revision and current.
        </p>
      ) : (
        <div className="space-y-1">
          {diffs.map((entry, i) => (
            <DiffEntryRow key={`${entry.kind}-${entry.path}-${i}`} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}

function SecretsSection() {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">Secrets</h3>
      <p className="rounded border border-border/50 bg-muted/30 p-2 text-xs italic text-muted-foreground">
        Secrets changed. Previous values are not displayed — secrets are write-only and cannot be
        restored from history.
      </p>
    </section>
  );
}

function GenericFieldSection({
  revision,
  currentValue,
}: {
  revision: ConfigRevision;
  currentValue: unknown;
}) {
  const diffs = diffJson(revision.previousValue, currentValue);
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">{fieldLabel(revision.field)}</h3>
      {diffs.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">No differences.</p>
      ) : (
        <div className="space-y-1">
          {diffs.map((entry, i) => (
            <DiffEntryRow key={`${entry.kind}-${entry.path}-${i}`} entry={entry} />
          ))}
        </div>
      )}
    </section>
  );
}

export function ConnectorDiffSheet({
  saveEvent,
  currentVars,
  open,
  onOpenChange,
  onRestore,
}: ConnectorDiffSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Save event</SheetTitle>
          {saveEvent && (
            <SheetDescription>
              {formatTimestamp(saveEvent.createdAt)}
              {saveEvent.createdByEmail ? ` · ${saveEvent.createdByEmail}` : " · system"}
            </SheetDescription>
          )}
        </SheetHeader>
        {saveEvent && (
          <div className="mt-4 space-y-6">
            {saveEvent.revisions.map((revision) => {
              if (revision.field === "vars") {
                return (
                  <VarsSection
                    key={revision.id}
                    revision={revision}
                    currentVars={currentVars}
                    onRestore={() => {
                      onRestore(revision.previousValue);
                      onOpenChange(false);
                    }}
                  />
                );
              }
              if (revision.field === "secrets") {
                return <SecretsSection key={revision.id} />;
              }
              // Fallback: generic per-field diff against an unknown current value.
              // The diff is only meaningful for `vars`; other fields just show "changed".
              return (
                <GenericFieldSection
                  key={revision.id}
                  revision={revision}
                  currentValue={revision.previousValue}
                />
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
